import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../styles/patternPulse.css';

// Seeded pseudo-random generator (mulberry32) so all players get the same daily sequence.
function mulberry32(seed) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6D2B79F5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function getDailySeed() {
  const d = new Date();
  const str = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  return str.split('').reduce((acc, c) => ((acc * 31) + c.charCodeAt(0)) | 0, 7);
}

const GRID_SIZE = 16;
const BASE_SEQ_LEN = 3;
const MAX_MISTAKES = 3;
const FLASH_ON_MS = 480;
const FLASH_OFF_MS = 220;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// ---------------------------------------------------------------------------
// PatternPulse
//
// Props:
//   onClose        — close the modal
//   onStateChange  — (state) => void  called on every game state change (student)
//   isSpectator    — render read-only view synced to spectatorState (teacher)
//   spectatorState — game state object received from data channel (teacher)
//   studentName    — displayed in spectator header
// ---------------------------------------------------------------------------
export default function PatternPulse({
  onClose,
  onStateChange,
  isSpectator = false,
  spectatorState = null,
  studentName = 'Student',
}) {
  const masterSeqRef = useRef(null);
  if (!masterSeqRef.current) {
    const rand = mulberry32(getDailySeed());
    masterSeqRef.current = Array.from({ length: 40 }, () => Math.floor(rand() * GRID_SIZE));
  }

  const cancelRef = useRef(false);

  const [phase, setPhase] = useState('idle');
  const [level, setLevel] = useState(1);
  const [score, setScore] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [playerSeq, setPlayerSeq] = useState([]);
  const [flashTile, setFlashTile] = useState(null);
  const [hitTile, setHitTile] = useState(null);
  const [missTile, setMissTile] = useState(null);
  const [bestScore, setBestScore] = useState(
    () => parseInt(localStorage.getItem('pp_best') || '0', 10)
  );

  const phaseRef = useRef(phase);
  const levelRef = useRef(level);
  const scoreRef = useRef(score);
  const mistakesRef = useRef(mistakes);
  const playerSeqRef = useRef(playerSeq);

  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { levelRef.current = level; }, [level]);
  useEffect(() => { scoreRef.current = score; }, [score]);
  useEffect(() => { mistakesRef.current = mistakes; }, [mistakes]);
  useEffect(() => { playerSeqRef.current = playerSeq; }, [playerSeq]);

  useEffect(() => {
    cancelRef.current = false;
    return () => { cancelRef.current = true; };
  }, []);

  // ── Broadcast game state to teacher via data channel ─────────────────────
  useEffect(() => {
    if (isSpectator || !onStateChange) return;
    onStateChange({ phase, level, score, mistakes, playerSeq, flashTile, hitTile, missTile });
  }, [isSpectator, onStateChange, phase, level, score, mistakes, playerSeq, flashTile, hitTile, missTile]);

  const getSeqForLevel = (lvl) =>
    masterSeqRef.current.slice(0, BASE_SEQ_LEN + lvl - 1);

  const playSequence = useCallback(async (seq) => {
    setPhase('showing');
    setPlayerSeq([]);
    setFlashTile(null);
    for (const tile of seq) {
      if (cancelRef.current) return;
      await sleep(FLASH_OFF_MS);
      if (cancelRef.current) return;
      setFlashTile(tile);
      await sleep(FLASH_ON_MS);
      if (cancelRef.current) return;
      setFlashTile(null);
    }
    await sleep(350);
    if (cancelRef.current) return;
    setPhase('input');
  }, []);

  const handleTileClick = useCallback((idx) => {
    if (phaseRef.current !== 'input') return;

    const lvl = levelRef.current;
    const sc = scoreRef.current;
    const err = mistakesRef.current;
    const pSeq = playerSeqRef.current;
    const seq = masterSeqRef.current.slice(0, BASE_SEQ_LEN + lvl - 1);
    const pos = pSeq.length;

    if (idx === seq[pos]) {
      setHitTile(idx);
      setTimeout(() => setHitTile(null), 320);
      const newPSeq = [...pSeq, idx];
      setPlayerSeq(newPSeq);

      if (newPSeq.length === seq.length) {
        const pts = seq.length * lvl * 10;
        const newScore = sc + pts;
        setScore(newScore);
        setBestScore((prev) => {
          const next = Math.max(prev, newScore);
          localStorage.setItem('pp_best', String(next));
          return next;
        });
        const nextLvl = lvl + 1;
        setLevel(nextLvl);
        setTimeout(() => {
          playSequence(masterSeqRef.current.slice(0, BASE_SEQ_LEN + nextLvl - 1));
        }, 650);
      }
    } else {
      setMissTile(idx);
      setTimeout(() => setMissTile(null), 500);
      const newErr = err + 1;
      setMistakes(newErr);

      if (newErr >= MAX_MISTAKES) {
        setTimeout(() => setPhase('gameover'), 750);
      } else {
        setTimeout(() => {
          setPlayerSeq([]);
          playSequence(masterSeqRef.current.slice(0, BASE_SEQ_LEN + levelRef.current - 1));
        }, 950);
      }
    }
  }, [playSequence]);

  const handleStart = () => {
    setLevel(1);
    setScore(0);
    setMistakes(0);
    setPlayerSeq([]);
    playSequence(getSeqForLevel(1));
  };

  const handleRestart = () => {
    setLevel(1);
    setScore(0);
    setMistakes(0);
    setPlayerSeq([]);
    playSequence(getSeqForLevel(1));
  };

  const shareResult = () => {
    const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const heartsLeft = MAX_MISTAKES - mistakes;
    const text =
      `🧠 Pattern Pulse · ${today}\n` +
      `Level ${level > 1 ? level - 1 : 1} · ${score} pts\n` +
      `${'❤️'.repeat(heartsLeft)}${'🖤'.repeat(mistakes)}\n` +
      `Play on Minversity!`;
    if (navigator.share) {
      navigator.share({ title: 'Pattern Pulse', text });
    } else {
      navigator.clipboard?.writeText(text).then(() => alert('Score copied!'));
    }
  };

  // ── Spectator: use synced state instead of local state ───────────────────
  const s = isSpectator ? spectatorState : null;
  const dPhase     = s ? (s.phase     ?? 'idle') : phase;
  const dLevel     = s ? (s.level     ?? 1)      : level;
  const dScore     = s ? (s.score     ?? 0)      : score;
  const dMistakes  = s ? (s.mistakes  ?? 0)      : mistakes;
  const dPlayerSeq = s ? (s.playerSeq ?? [])     : playerSeq;
  const dFlashTile = s ? (s.flashTile ?? null)   : flashTile;
  const dHitTile   = s ? (s.hitTile   ?? null)   : hitTile;
  const dMissTile  = s ? (s.missTile  ?? null)   : missTile;

  const seqLen = BASE_SEQ_LEN + dLevel - 1;
  const isGameRunning = dPhase === 'showing' || dPhase === 'input';

  return (
    <div
      className="pp-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Pattern Pulse game"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="pp-modal">
        {/* Header */}
        <div className="pp-header">
          <div className="pp-title-row">
            <span className="pp-icon" aria-hidden="true">🧠</span>
            <h2 className="pp-title">Pattern Pulse</h2>
            {isSpectator ? (
              <span className="pp-badge pp-badge--spectator">Watching {studentName}</span>
            ) : (
              <span className="pp-badge">Daily</span>
            )}
          </div>
          <button className="pp-close" onClick={onClose} aria-label="Close game">✕</button>
        </div>

        {/* Stats bar */}
        {isGameRunning && (
          <div className="pp-stats">
            <div className="pp-stat">
              <span className="pp-stat-label">Level</span>
              <span className="pp-stat-value">{dLevel}</span>
            </div>
            <div className="pp-stat">
              <span className="pp-stat-label">Score</span>
              <span className="pp-stat-value">{dScore}</span>
            </div>
            <div className="pp-mistakes" aria-label={`${dMistakes} of ${MAX_MISTAKES} mistakes`}>
              {Array.from({ length: MAX_MISTAKES }, (_, i) => (
                <span key={i} className={`pp-dot${i < dMistakes ? ' pp-dot--used' : ''}`} />
              ))}
            </div>
          </div>
        )}

        {/* Arena */}
        <div className="pp-arena">
          {isGameRunning && (
            <div className="pp-hud">
              {dPhase === 'showing' && (
                <span>{isSpectator ? `${studentName} is watching the sequence…` : 'Watch the sequence…'}</span>
              )}
              {dPhase === 'input' && (
                <span>
                  {isSpectator ? `${studentName} is entering —` : 'Tap it back —'}{' '}
                  <strong>{seqLen - dPlayerSeq.length}</strong> remaining
                </span>
              )}
            </div>
          )}

          <div className={`pp-grid${dPhase === 'gameover' ? ' pp-grid--dim' : ''}`}>
            {Array.from({ length: GRID_SIZE }, (_, i) => (
              <button
                key={i}
                className={[
                  'pp-tile',
                  dFlashTile === i ? 'pp-tile--flash' : '',
                  dHitTile === i   ? 'pp-tile--hit'   : '',
                  dMissTile === i  ? 'pp-tile--miss'  : '',
                  !isSpectator && dPhase === 'input' ? 'pp-tile--interactive' : '',
                ].filter(Boolean).join(' ')}
                onClick={isSpectator ? undefined : () => handleTileClick(i)}
                disabled={isSpectator || dPhase !== 'input'}
                aria-label={`Tile ${i + 1}`}
              />
            ))}
          </div>

          {/* Spectator waiting state */}
          {isSpectator && dPhase === 'idle' && (
            <div className="pp-splash">
              <p className="pp-splash-copy">
                Waiting for {studentName} to start the game…
              </p>
            </div>
          )}

          {/* Student start splash */}
          {!isSpectator && dPhase === 'idle' && (
            <div className="pp-splash">
              <p className="pp-splash-copy">
                A sequence flashes across the grid.<br />
                Tap it back in order. Same puzzle for everyone today.
              </p>
              <button className="pp-cta" onClick={handleStart}>
                Start Today's Pulse
              </button>
              {bestScore > 0 && (
                <p className="pp-personal-best">Personal best: {bestScore} pts</p>
              )}
            </div>
          )}

          {/* Game over */}
          {dPhase === 'gameover' && (
            <div className="pp-gameover">
              <div className="pp-gameover-emoji">💥</div>
              <h3 className="pp-gameover-heading">Game Over</h3>
              <p className="pp-gameover-score">{dScore} pts</p>
              <p className="pp-gameover-sub">Reached level {dLevel > 1 ? dLevel - 1 : 1}</p>
              {!isSpectator && dScore > 0 && dScore >= bestScore && (
                <p className="pp-new-best">New personal best 🏆</p>
              )}
              {!isSpectator && (
                <div className="pp-gameover-actions">
                  <button className="pp-cta" onClick={handleRestart}>Play Again</button>
                  <button className="pp-share" onClick={shareResult}>Share Score</button>
                </div>
              )}
              {isSpectator && (
                <p className="pp-splash-copy" style={{ marginTop: '0.75rem' }}>
                  {studentName} finished the game.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
